import {
  ComboDetailPayload,
  DiscoveryProduct,
  DiscoveryPromotion,
  BenefitItems,
  BenefitVoucher,
  ProductDetailProductGroupVariantAttrValue,
} from '@teko/discovery';
import { useServices, useTenantConfig } from 'contexts';
import { convertProductDetail } from 'helpers';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { ComboItem, ComboProduct } from 'types';
import {
  useDiscoveryProduct,
  useRecommendationComboDetail,
} from './useDiscovery';
import { IVariantItem } from '@teko/ecommerce';
import { stringToInteger } from '@teko/utils';

export const useDetailCombo = (
  type: ComboItem['type'],
  payload: ComboDetailPayload,
) => {
  const [products, setProducts] = useState<ComboProduct[]>([]);
  const [payloadCombo, setPayloadCombo] = useState<ComboDetailPayload>(payload);
  const [comboPromotion, setComboPromotion] = useState<DiscoveryPromotion>();
  const [supplierRetailPrice, setSupplierRetailPrice] = useState<number>();
  const [latestPrice, setLatestPrice] = useState<number>();
  const skus = (payloadCombo?.comboLineItems || []).map((el) => el.sku);
  const { location } = useTenantConfig();
  const { discoveryService } = useServices();
  const fetcherListProduct = (): Promise<DiscoveryProduct[]> => {
    return Promise.all(
      skus.map(
        (sku) =>
          discoveryService.getProduct({
            sku,
            location,
          }) as unknown as DiscoveryProduct,
      ),
    );
  };

  const { data: dataAutogenCombo, error: errorAutogen } = useSWR<
    DiscoveryProduct[]
  >(
    type === 'autogen' && skus?.length
      ? [`${discoveryService.getBaseUrl}/list-product`, JSON.stringify(skus)]
      : null,
    fetcherListProduct,
    { revalidateOnFocus: false },
  );

  const {
    result: { combo: dataDynamicCombo },
    isLoading: isLoadingDynamicCombo,
  } = useRecommendationComboDetail(payloadCombo, type === 'dynamic');

  const updatePrice = (newProducts: ComboProduct[]) => {
    setSupplierRetailPrice(
      newProducts.reduce((acc, curr) => {
        return acc + (curr.quantity || 0) * curr.supplierRetailPrice;
      }, 0),
    );
    setLatestPrice(
      newProducts.reduce((acc, curr) => {
        return acc + (curr.quantity || 0) * curr.latestPrice;
      }, 0),
    );
  };

  const onChangeQuantity = (sku: string, value: number) => {
    if (type === 'autogen') {
      const newProducts = products.map((el) => {
        if (el.sku === sku) {
          return {
            ...el,
            quantity: value,
          };
        }
        return el;
      });
      setProducts(newProducts);
      updatePrice(newProducts);
    }
  };

  const onChangeAutogenProduct = (index: number, newProduct: ComboProduct) => {
    const newProducts = products.map((el, idx) => {
      if (index === idx) return newProduct;
      return el;
    });
    setProducts(newProducts);
    setPayloadCombo({
      comboLineItems: newProducts.map((prod) => ({
        sku: prod.sku,
        sellerSku: prod.sellerSku,
        quantity: prod.quantity,
        selectPromotionId: prod.selectionPromotionId,
      })),
    });
    updatePrice(newProducts);
  };

  const onChangeDynamicProduct = (newPayload: ComboDetailPayload) => {
    setPayloadCombo(newPayload);
  };

  const convertProductsAutogenCombo = (): void => {
    if (!dataAutogenCombo) return;
    const newProducts = dataAutogenCombo.map((prod) => {
      const sku = prod.productInfo.sku;
      const quantity =
        payloadCombo.comboLineItems.find((line) => line.sku === sku)
          ?.quantity || 1;
      const convertedProduct = convertProductDetail(prod);
      const selectionPromotion = convertedProduct?.selectionPromotions?.[0];
      let gifts: BenefitItems[] = [];
      const vouchers = [];
      (prod.defaultPromotions || []).forEach((el) => {
        if (!!el?.benefit?.gifts?.length) {
          gifts = [...gifts, ...el.benefit.gifts];
        }
        if (el?.benefit?.voucher?.name) {
          vouchers.push(el.benefit.voucher);
        }
      });
      if (selectionPromotion) {
        if (!!selectionPromotion?.benefit?.gifts?.length) {
          gifts = [...gifts, ...selectionPromotion.benefit.gifts];
        }
        if (selectionPromotion?.benefit?.voucher?.name) {
          vouchers.push(selectionPromotion.benefit.voucher);
        }
      }

      return {
        sku,
        quantity,
        image: prod.productInfo.imageUrl,
        name: prod.productInfo.name,
        selectedVariant: (convertedProduct.selected || []).map((v) => v.value),
        supplierRetailPrice:
          Number(prod?.prices?.[0]?.supplierRetailPrice) || 0,
        latestPrice: Number(prod?.prices?.[0]?.latestPrice) || 0,
        selectionPromotionId: selectionPromotion?.id,
        defaultPromotions: convertedProduct?.defaultPromotions || [],
        selectionPromotion,
        selectionPromotions: convertedProduct?.selectionPromotions || [],
        allowSelectPromotion:
          convertedProduct?.selectionPromotions?.length &&
          convertedProduct?.selectionPromotions?.length > 0,
        totalAvailable: prod.totalAvailable,
        gifts,
        vouchers,
      } as ComboProduct;
    });
    setProducts(newProducts);
    updatePrice(newProducts);
  };

  const convertProductsDynamicCombo = (): void => {
    if (!dataDynamicCombo) return;

    const newProducts = dataDynamicCombo.products.map((comboItem) => {
      const sku = comboItem?.product.productInfo.sku;
      const sellerSku = comboItem?.product.productInfo.sellerSku;
      const quantity = comboItem?.quantity;
      const convertedProduct = comboItem?.product
        ? convertProductDetail(comboItem?.product)
        : null;
      let gifts: BenefitItems[] = [];
      const vouchers: BenefitVoucher[] = [];
      (comboItem?.product.promotions || []).forEach((el) => {
        if (!!el?.benefit?.gifts?.length) {
          gifts = [...gifts, ...el.benefit.gifts];
        }
        if (el?.benefit?.voucher?.name) {
          vouchers.push(el.benefit.voucher);
        }
      });
      return {
        sku,
        sellerSku,
        quantity,
        image: comboItem?.product.productInfo.imageUrl,
        name: comboItem?.product.productInfo.name,
        selectedVariant: (convertedProduct?.selected || []).map((v) => v.value),
        supplierRetailPrice:
          Number(comboItem?.product?.prices?.[0]?.supplierRetailPrice) || 0,
        latestPrice: Number(comboItem?.product?.prices?.[0]?.latestPrice) || 0,
        defaultPromotions: comboItem?.product.promotions || [],
        selectionPromotion: null,
        selectionPromotions: [],
        allowSelectPromotion: false,
        totalAvailable: comboItem?.product.totalAvailable,
        gifts,
        vouchers,
      } as ComboProduct;
    });
    setComboPromotion({
      ...dataDynamicCombo.promotion,
      id: dataDynamicCombo.id,
    });
    setProducts(newProducts);
    setSupplierRetailPrice(Number(dataDynamicCombo.supplierRetailPrice) || 0);
    setLatestPrice(Number(dataDynamicCombo.latestPrice) || 0);
  };

  const onFinish = (
    callback: (
      comboType: ComboItem['type'],
      newPayloadCombo: ComboDetailPayload,
    ) => void,
  ) => {
    callback(type, payloadCombo);
  };

  const convertProducts = () => {
    if (type === 'autogen') return convertProductsAutogenCombo();
    else return convertProductsDynamicCombo();
  };

  useEffect(() => {
    setPayloadCombo(payload);
  }, [JSON.stringify(payload)]);

  useEffect(() => {
    convertProducts();
  }, [JSON.stringify(dataAutogenCombo), JSON.stringify(dataDynamicCombo)]);

  return {
    payloadCombo,
    products,
    latestPrice,
    supplierRetailPrice,
    onChangeQuantity,
    comboPromotion,
    onChangeAutogenProduct,
    onChangeDynamicProduct,
    onFinish,
    loadingCombo:
      type === 'autogen'
        ? !errorAutogen && !dataAutogenCombo
        : isLoadingDynamicCombo,
  };
};

const useAutogenPrices = (
  sku: string,
  supplierRetailPrice: number,
  selectPromotionId?: number,
) => {
  const { terminal } = useTenantConfig();
  const { cartServiceV2 } = useServices();
  const { data: calculatePrice } = useSWR(
    !!sku && !!terminal
      ? [`cart_service/calculate_price`, sku, terminal, selectPromotionId]
      : null,
    async (_: string, sku: string, selectPromotionId: number) => {
      return await cartServiceV2.calculatePrice({ sku, selectPromotionId });
    },
    {
      revalidateOnFocus: false,
    },
  );

  const {
    data: {
      saveWithoutSelectionPromotion,
      priceWithoutSelectionPromotion,
    } = {},
  } = useSWR<{
    saveWithoutSelectionPromotion?: number;
    priceWithoutSelectionPromotion?: number;
  }>(
    !!sku && !!terminal
      ? [
          `cart_service/calculate_save_without_selection_promotion`,
          sku,
          terminal,
        ]
      : null,
    async (
      _: string,
      sku: string,
    ): Promise<{
      saveWithoutSelectionPromotion?: number;
      priceWithoutSelectionPromotion?: number;
    }> => {
      const { price: priceWithoutSelectionPromotion } =
        await cartServiceV2.calculatePrice({
          sku,
          selectPromotionId: undefined,
        });
      if (
        !isNaN(+supplierRetailPrice) &&
        !isNaN(+priceWithoutSelectionPromotion)
      )
        return {
          saveWithoutSelectionPromotion:
            +supplierRetailPrice - priceWithoutSelectionPromotion,
          priceWithoutSelectionPromotion,
        };
      return {
        saveWithoutSelectionPromotion: undefined,
        priceWithoutSelectionPromotion: undefined,
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  return {
    latestPrice: calculatePrice?.price,
    saveWithoutSelectionPromotion,
    priceWithoutSelectionPromotion,
  };
};

export const useAutogenSelectedProduct = (
  index: number,
  prod: ComboProduct,
) => {
  const [skuVariant, setSkuVariant] = useState(prod.sku);
  const [selectionPromotionId, setSelectionPromotionId] = useState(
    prod.selectionPromotionId,
  );
  const [quantity, setQuantity] = useState(prod.quantity);
  const [convertProduct, setConvertProduct] = useState<ComboProduct>();
  const {
    result: { product },
    isLoading,
  }: { result: { product: DiscoveryProduct }; isLoading: boolean } =
    useDiscoveryProduct(skuVariant);
  const {
    latestPrice,
    priceWithoutSelectionPromotion,
    saveWithoutSelectionPromotion,
  } = useAutogenPrices(
    prod?.sku,
    prod?.supplierRetailPrice,
    selectionPromotionId,
  );

  const onChangeVariant = (variant: IVariantItem, index: number) => {
    const newSku = getNewVariant(product, variant, index);
    if (!!newSku) {
      setSkuVariant(newSku);
    }
  };

  useEffect(() => {
    if (!product || !latestPrice) return;
    const convertedProduct = convertProductDetail(product);
    const selectionPromotion =
      (convertedProduct?.selectionPromotions || []).find(
        (el) => el.id === selectionPromotionId,
      ) || null;
    let gifts: BenefitItems[] = [];
    const vouchers: BenefitVoucher[] = [];
    (product.defaultPromotions || []).forEach((el) => {
      if (!!el?.benefit?.gifts?.length) {
        gifts = [...gifts, ...el.benefit.gifts];
      }
      if (el?.benefit?.voucher?.name) {
        vouchers.push(el.benefit.voucher);
      }
    });
    const newProduct = {
      sku: skuVariant,
      sellerSku: product.productInfo.sellerSku,
      quantity,
      image: product.productInfo.imageUrl,
      name: product.productInfo.name,
      selectedVariant: (convertedProduct.selected || []).map((v) => v.value),
      supplierRetailPrice:
        Number(product?.prices?.[0]?.supplierRetailPrice) || 0,
      latestPrice,
      selectionPromotionId,
      defaultPromotions: convertedProduct?.defaultPromotions || [],
      selectionPromotion,
      selectionPromotions: convertedProduct?.selectionPromotions || [],
      allowSelectPromotion: true,
      totalAvailable: product.totalAvailable,
      gifts,
      vouchers,
      saveWithoutSelectionPromotion,
      productConfigurations: convertedProduct?.productGroup?.configurations,
      priceWithoutSelectionPromotion,
      uomName: convertedProduct.uomName || '',
    };
    setConvertProduct(newProduct);
  }, [
    JSON.stringify(product),
    selectionPromotionId,
    quantity,
    latestPrice,
    saveWithoutSelectionPromotion,
    priceWithoutSelectionPromotion,
  ]);

  const onFinish = (callback: (idx: number, newProd: ComboProduct) => void) => {
    if (!convertProduct) return;
    callback(index, convertProduct);
  };

  return {
    product: convertProduct,
    onChangeVariant,
    onChangeQuantity: setQuantity,
    onChangeSelectionPromotionId: setSelectionPromotionId,
    onFinish,
    loading: isLoading,
    selectionPromotionId,
  };
};

export const useDynamicSelectedProduct = ({
  payload,
  selectedProductIdx,
}: {
  payload: ComboDetailPayload;
  selectedProductIdx: number;
}) => {
  const [payloadCombo, setPayloadCombo] = useState(payload);
  const {
    result: { combo: dataDynamicCombo },
    isLoading: isLoadingDynamicCombo,
  } = useRecommendationComboDetail(payloadCombo, true);
  const [convertProduct, setConvertProduct] = useState<ComboProduct>();

  const onChangeVariant = (variant: IVariantItem, variantIndex: number) => {
    const product = dataDynamicCombo?.products[selectedProductIdx]?.product;

    if (!product) return;
    const newSku = getNewVariant(product, variant, variantIndex);

    if (!!newSku) {
      const newPayloadCombo = { ...payloadCombo };
      newPayloadCombo.comboLineItems = payloadCombo.comboLineItems.map(
        (lineItem, index) => {
          if (selectedProductIdx === index)
            return {
              sku: newSku,
              sellerSku: lineItem.sellerSku,
              quantity: lineItem.quantity,
            };
          return lineItem;
        },
      );
      setPayloadCombo(newPayloadCombo);
    }
  };

  const onFinish = (callback: (newPayload: ComboDetailPayload) => void) => {
    callback(payloadCombo);
  };

  useEffect(() => {
    if (!dataDynamicCombo) return;
    const comboProduct = dataDynamicCombo.products[selectedProductIdx];

    if (!comboProduct) return;
    const product = comboProduct.product;
    const convertedProduct = convertProductDetail(product);
    let gifts: BenefitItems[] = [];
    const vouchers: BenefitVoucher[] = [];
    (product.defaultPromotions || []).forEach((el) => {
      if (!!el?.benefit?.gifts?.length) {
        gifts = [...gifts, ...el.benefit.gifts];
      }
      if (el?.benefit?.voucher?.name) {
        vouchers.push(el.benefit.voucher);
      }
    });
    const supplierRetailPrice =
      Number(product?.prices?.[0]?.supplierRetailPrice) || 0;
    const latestPrice = Number(product?.prices?.[0]?.latestPrice) || 0;
    const newProduct = {
      sku: product.productInfo.sku,
      sellerSku: product.productInfo.sellerSku,
      quantity: comboProduct.quantity,
      image: product.productInfo.imageUrl,
      name: product.productInfo.name,
      selectedVariant: (convertedProduct.selected || []).map((v) => v.value),
      supplierRetailPrice,
      latestPrice,
      defaultPromotions: product?.promotions || [],
      selectionPromotion: null,
      selectionPromotions: [],
      allowSelectPromotion: false,
      totalAvailable: product.totalAvailable,
      gifts,
      vouchers,
      saveWithoutSelectionPromotion: supplierRetailPrice - latestPrice,
      productConfigurations: convertedProduct.productGroup?.configurations,
      uomName: convertedProduct.uomName || '',
    } as ComboProduct;
    setConvertProduct(newProduct);
  }, [JSON.stringify(dataDynamicCombo), selectedProductIdx]);

  return {
    product: convertProduct,
    onChangeVariant,
    onFinish,
    loading: isLoadingDynamicCombo,
  };
};

const getNewVariant = (
  product: DiscoveryProduct,
  variant: IVariantItem,
  index: number,
) => {
  const convertedProduct = convertProductDetail(product);
  if (!convertedProduct?.selected?.length) {
    return;
  }
  const selectedVariants: ProductDetailProductGroupVariantAttrValue[] = [];

  const optionId =
    typeof variant.optionId === 'number'
      ? variant.optionId
      : (stringToInteger(variant.optionId, 0) as number);

  const att: ProductDetailProductGroupVariantAttrValue = {
    code: convertedProduct.productGroup?.configurations?.[index].code || '',
    value: variant.value,
    id: optionId,
    optionId,
  };
  for (
    let i = 0;
    i < (convertedProduct?.productGroup?.configurations?.length || 0);
    i++
  ) {
    if (convertedProduct.selected[i]?.code === att.code) {
      selectedVariants.push(att);
    } else {
      selectedVariants.push(convertedProduct.selected[i]);
    }
  }
  if (!selectedVariants.length) {
    return;
  }
  let max = 0;
  let newSku = '';
  convertedProduct.productGroup?.variants?.forEach((variant) => {
    let countAtt = 0;
    for (let i = 0; i < variant.attributeValues.length; i++) {
      if (
        variant.attributeValues[i]?.optionId === selectedVariants[i]?.optionId
      ) {
        countAtt++;
      } else {
        break;
      }
    }
    if (countAtt > max) {
      max = countAtt;
      newSku = variant.sku;
    }
  });
  if (newSku && newSku !== convertedProduct.sku) return newSku;
};
